import React, { useState, useEffect } from "react";
// Firebase operations extracted to src/hooks/useCustomers
import { Plus, Search, Edit, UserPlus, Loader2, ToggleLeft, ToggleRight } from "lucide-react";
import { useCustomers } from "../hooks/useCustomers";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import CustomerModal from "../components/modals/CustomerModal";

export default function Customers() {
  
  const {
    customers,
    categories,
    paymentMethods,
    loading,
    error,
    setError,
    addCustomer,
    updateCustomer
  } = useCustomers();

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  
  const [paySchedules, setPaySchedules] = useState([]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "pay_schedules"), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setPaySchedules(list);
    }, (err) => {
      console.error("Error loading pay schedules in Customers:", err);
    });
    return () => unsubscribe();
  }, []);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    category: "Enterprise",
    status: "Active",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    billAddress: "",
    billAddress2: "",
    billCity: "",
    billState: "",
    billZip: "",
    shipAddress: "",
    shipAddress2: "",
    shipCity: "",
    shipState: "",
    shipZip: "",
    markFor: "",
    preferredPaymentMethod: "",
    paySchedule: "",
    pocs: [{ name: "", title: "", email: "", phone: "" }]
  });

  function handleOpenAddModal() {
    setIsEditMode(false);
    setError("");
    setFormData({
      name: "",
      category: categories[0] || "Enterprise",
      status: "Active",
      address: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
      billAddress: "",
      billAddress2: "",
      billCity: "",
      billState: "",
      billZip: "",
      shipAddress: "",
      shipAddress2: "",
      shipCity: "",
      shipState: "",
      shipZip: "",
      markFor: "",
      preferredPaymentMethod: "",
      paySchedule: "",
      pocs: [{ name: "", title: "", email: "", phone: "" }]
    });
    setModalOpen(true);
  }

  function handleOpenEditModal(customer) {
    setIsEditMode(true);
    setCurrentCustomerId(customer.id);
    setError("");
    
    const normalizedPocs = customer.pocs && customer.pocs.length > 0 ? customer.pocs : [
      {
        name: customer.contactName || "",
        title: customer.pocTitle || "",
        email: customer.email || "",
        phone: customer.phone || ""
      }
    ];

    setFormData({
      name: customer.name,
      category: customer.category,
      status: customer.status,
      address: customer.address || "",
      address2: customer.address2 || "",
      city: customer.city || "",
      state: customer.state || "",
      zip: customer.zip || "",
      billAddress: customer.billAddress || "",
      billAddress2: customer.billAddress2 || "",
      billCity: customer.billCity || "",
      billState: customer.billState || "",
      billZip: customer.billZip || "",
      shipAddress: customer.shipAddress || "",
      shipAddress2: customer.shipAddress2 || "",
      shipCity: customer.shipCity || "",
      shipState: customer.shipState || "",
      shipZip: customer.shipZip || "",
      markFor: customer.markFor || "",
      preferredPaymentMethod: customer.preferredPaymentMethod || "",
      paySchedule: customer.paySchedule || "",
      pocs: normalizedPocs
    });
    setModalOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (isEditMode) {
        await updateCustomer(currentCustomerId, formData);
      } else {
        await addCustomer(formData);
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Error saving customer:", err);
      // setError is handled inside hook
    }
  }

  const filteredCustomers = customers.filter(customer => {
    const pocs = customer.pocs && customer.pocs.length > 0 ? customer.pocs : [
      { name: customer.contactName || "", email: customer.email || "", phone: customer.phone || "", title: customer.pocTitle || "" }
    ];
    
    const searchLower = searchTerm.toLowerCase();
    
    const matchesName = customer.name.toLowerCase().includes(searchLower);
    const matchesAddress = [customer.address, customer.city, customer.state, customer.zip]
      .filter(Boolean)
      .some(field => field.toLowerCase().includes(searchLower));
      
    const matchesPocs = pocs.some(poc => 
      (poc.name && poc.name.toLowerCase().includes(searchLower)) ||
      (poc.title && poc.title.toLowerCase().includes(searchLower)) ||
      (poc.email && poc.email.toLowerCase().includes(searchLower)) ||
      (poc.phone && poc.phone.toLowerCase().includes(searchLower))
    );
    
    const matchesSearch = matchesName || matchesAddress || matchesPocs;
    const matchesCategory = categoryFilter === "All" || customer.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Customers Management</h1>
          <p>Register, evaluate, and track customer profile details.</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          <Plus size={18} />
          Register Customer
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="table-actions-container">
        <div className="search-box-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search customers by name, contact, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-select-group">
          <select
            className="form-input filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="All">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Customer List Table */}
      {loading && customers.length === 0 ? (
        <div className="loading-container">
          <Loader2 className="spinner-icon" size={36} />
          <span>Loading customers database...</span>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="empty-state-card">
          <UserPlus size={48} className="empty-state-icon" />
          <h3>No Customers Found</h3>
          <p>Try refining your search terms or register a new customer partner.</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>Category</th>
                <th>Contact Person</th>
                <th>Contact Details</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => {
                const primaryPoc = (customer.pocs && customer.pocs.length > 0) ? customer.pocs[0] : {
                  name: customer.contactName || "",
                  title: customer.pocTitle || "",
                  email: customer.email || "",
                  phone: customer.phone || ""
                };
                const extraPocCount = customer.pocs ? customer.pocs.length - 1 : 0;

                return (
                  <tr key={customer.id}>
                    <td>
                      <div className="vendor-primary-name" style={{ color: "var(--text-primary)", fontWeight: 600 }}>{customer.name}</div>
                      {(customer.city || customer.state) && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                          {[customer.address, customer.city, customer.state].filter(Boolean).join(", ")}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-info">{customer.category}</span>
                      {customer.paySchedule && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.25rem" }}>
                          Pay: {customer.paySchedule}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="poc-name-text" style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                        {primaryPoc.name || <span className="text-secondary" style={{ fontStyle: "italic", fontWeight: "normal" }}>No Contact</span>}
                      </div>
                      {primaryPoc.title && <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{primaryPoc.title}</div>}
                      {extraPocCount > 0 && (
                        <span className="sku-badge" style={{ fontSize: "0.65rem", background: "rgba(255,255,255,0.05)", color: "var(--text-secondary)", marginTop: "0.2rem", display: "inline-block" }}>
                          +{extraPocCount} more POC{extraPocCount > 1 ? "s" : ""}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="vendor-contact-details">
                        <span style={{ color: "var(--text-primary)", display: "block" }}>{primaryPoc.email}</span>
                        <span className="phone-sub" style={{ color: "var(--text-secondary)", display: "block" }}>{primaryPoc.phone}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${customer.status === "Active" ? "badge-success" : "badge-danger"}`}>
                        {customer.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={() => handleOpenEditModal(customer)}
                          title="Edit Customer"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={async () => {
                            const newStatus = customer.status === "Active" ? "Inactive" : "Active";
                            try {
                              const docRef = doc(db, "customers", customer.id);
                              await updateDoc(docRef, { status: newStatus });
                              setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, status: newStatus } : c));
                            } catch (err) {
                              console.error("Error toggling customer status:", err);
                            }
                          }}
                          title={`Toggle status to ${customer.status === "Active" ? "Inactive" : "Active"}`}
                          style={{ border: "none", background: "none", padding: 0 }}
                        >
                          {customer.status === "Active" ? (
                            <ToggleRight size={26} style={{ color: "var(--success)" }} />
                          ) : (
                            <ToggleLeft size={26} style={{ color: "var(--text-muted)" }} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Register/Edit Customer Modal */}
      <CustomerModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        isEditMode={isEditMode}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        error={error}
        categories={categories}
        paymentMethods={paymentMethods}
        paySchedules={paySchedules}
        loading={loading}
      />
    </div>
  );
}
